<template>
  <div class="py-4" tabindex="0" @keydown="teclado" ref="mesa">
    <!-- Encabezado -->
    <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px">
      <v-btn icon :to="{ name: 'empresa.productos.centroCarga' }"><v-icon>mdi-arrow-left</v-icon></v-btn>
      <div>
        <div class="text-h6 font-weight-bold">{{ carga ? carga.nombre : "" }}</div>
        <div class="caption grey--text" v-if="carga">
          {{ carga.proveedor_nombre || "Sin proveedor" }} · {{ carga.sucursales_nombres.join(", ") }}
        </div>
      </div>
      <v-spacer />
      <v-autocomplete
        v-if="esFactura && !carga.proveedor"
        v-model="proveedorFactura"
        :items="proveedores"
        :search-input.sync="buscarProveedor"
        item-text="nombre"
        item-value="id"
        placeholder="¿De qué proveedor es la factura?"
        dense
        outlined
        hide-details
        no-filter
        style="max-width: 260px"
        @change="elegirProveedor"
      />
      <v-btn
        v-if="esFactura"
        depressed
        color="green"
        dark
        class="text-none"
        :disabled="!puedeRegistrarCompra"
        :title="puedeRegistrarCompra ? '' : 'Se habilita cuando todas las filas están aplicadas'"
        @click="registrarCompra"
      >
        Registrar la compra
      </v-btn>
      <v-btn
        outlined
        class="text-none"
        :disabled="!carga || !carga.conteos.seguras"
        @click="aceptarSeguras"
      >
        Aceptar todas las seguras
      </v-btn>
      <v-btn depressed color="primary" class="text-none" @click="abrirVistaPrevia">
        Vista previa y aplicar
      </v-btn>
    </div>

    <!-- Filtros -->
    <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px">
      <v-chip-group v-model="filtro" mandatory active-class="primary--text" @change="recargar">
        <v-chip value="todas" small filter outlined>Todas <span v-if="carga" class="ml-1">({{ carga.conteos.total }})</span></v-chip>
        <v-chip value="sin_decidir" small filter outlined>Sin decidir <span v-if="carga" class="ml-1">({{ carga.conteos.sin_decidir }})</span></v-chip>
        <v-chip value="SEGURO" small filter outlined color="green">Seguras</v-chip>
        <v-chip value="DUDOSO" small filter outlined color="orange">Dudosas</v-chip>
        <v-chip value="NUEVO" small filter outlined color="blue">Nuevas</v-chip>
      </v-chip-group>
      <v-spacer />
      <v-text-field
        v-model="busqueda"
        dense
        outlined
        hide-details
        clearable
        prepend-inner-icon="mdi-magnify"
        placeholder="Buscar en la carga"
        style="max-width: 260px"
        @input="buscarConPausa"
      />
    </div>

    <!-- Edición en lote -->
    <v-card v-if="seleccion.length" outlined class="pa-3 mb-3 d-flex align-center flex-wrap" style="gap: 8px">
      <span class="font-weight-bold">{{ seleccion.length }} filas</span>
      <v-btn small outlined class="text-none" @click="lote({ decision: 'EXISTENTE' })">Es el encontrado</v-btn>
      <v-btn small outlined class="text-none" @click="lote({ decision: 'NUEVO' })">Dar de alta</v-btn>
      <v-btn small outlined class="text-none" @click="lote({ decision: 'IGNORAR' })">Ignorar</v-btn>
      <v-text-field v-model="loteMargen" dense outlined hide-details suffix="%" placeholder="Margen" style="max-width: 110px" />
      <v-select v-model="loteCategoria" :items="categorias" item-text="nombre" item-value="id" dense outlined hide-details clearable placeholder="Categoría" style="max-width: 220px" />
      <v-select v-model="loteAlicuota" :items="alicuotas" item-text="descripcion" item-value="id" dense outlined hide-details clearable placeholder="IVA" style="max-width: 110px" />
      <v-btn small depressed color="primary" class="text-none" :disabled="!loteMargen && !loteCategoria && !loteAlicuota" @click="loteValores">Aplicar a las filas</v-btn>
      <v-spacer />
      <v-btn small text class="text-none" @click="seleccion = []">Quitar selección</v-btn>
    </v-card>

    <div class="caption grey--text mb-2">
      Teclado: ↑ ↓ moverse · Enter acepta lo propuesto · N dar de alta · I ignorar · Espacio seleccionar
    </div>

    <v-row dense>
      <!-- Mesa -->
      <v-col cols="12" md="7">
        <v-card outlined>
          <v-progress-linear v-if="loading" indeterminate />
          <v-simple-table dense class="mesa">
            <thead>
              <tr>
                <th style="width: 32px"></th>
                <th>Producto del proveedor</th>
                <th class="text-right">Costo</th>
                <th>Resolución</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(l, i) in lineas"
                :key="l.id"
                :class="{ 'fila--foco': i === foco, 'fila--aplicada': l.aplicada }"
                @click="foco = i"
              >
                <td>
                  <v-simple-checkbox :value="seleccion.includes(l.id)" :disabled="l.aplicada" @input="alternar(l)" />
                </td>
                <td>
                  <div class="d-flex align-center">
                    <v-icon x-small :color="colorEstado(l.estado)" class="mr-1">mdi-circle</v-icon>
                    <span class="body-2">{{ l.descripcion }}</span>
                  </div>
                  <div class="caption grey--text">
                    {{ l.codigo }}<span v-if="l.codigo_barras"> · {{ l.codigo_barras }}</span>
                    <v-icon v-for="a in l.avisos" :key="a.tipo" x-small color="orange" class="ml-1" :title="textoAviso(a)">mdi-alert</v-icon>
                  </div>
                </td>
                <td class="text-right">
                  {{ l.costo !== null ? `$${Number(l.costo).toFixed(2)}` : "—" }}
                  <div v-if="l.cantidad != null" class="caption grey--text">× {{ Number(l.cantidad) }}</div>
                </td>
                <td class="caption">
                  <span v-if="l.aplicada" class="grey--text">Aplicada</span>
                  <span v-else-if="l.decision === 'EXISTENTE'" class="green--text">{{ l.presentacion_descripcion }}</span>
                  <span v-else-if="l.decision === 'NUEVO'" class="blue--text">Alta: {{ l.nuevo_nombre }}</span>
                  <span v-else-if="l.decision === 'IGNORAR'" class="grey--text">Ignorada</span>
                  <span v-else-if="l.estado === 'SEGURO'">¿{{ l.presentacion_descripcion }}?</span>
                  <span v-else-if="l.estado === 'DUDOSO'" class="orange--text">{{ l.candidatos.length }} parecidos</span>
                  <span v-else class="blue--text">No está en el catálogo</span>
                </td>
              </tr>
              <tr v-if="!lineas.length && !loading">
                <td colspan="4" class="caption grey--text">Sin filas en este filtro.</td>
              </tr>
            </tbody>
          </v-simple-table>
          <div class="d-flex justify-center py-2" v-if="total > lineas.length">
            <v-btn small text class="text-none" :loading="loading" @click="masFilas">Ver más ({{ total - lineas.length }})</v-btn>
          </div>
        </v-card>

        <!-- Alta manual -->
        <v-card outlined class="pa-3 mt-3 d-flex align-center flex-wrap" style="gap: 8px">
          <v-text-field v-model="manual.descripcion" dense outlined hide-details placeholder="Producto" style="min-width: 200px" />
          <v-text-field v-model="manual.codigo_barras" dense outlined hide-details placeholder="Código de barras" style="max-width: 160px" />
          <v-text-field v-model="manual.costo" dense outlined hide-details placeholder="Costo" style="max-width: 110px" />
          <v-btn small outlined class="text-none" :disabled="!manual.descripcion" @click="agregarManual">Agregar fila</v-btn>
        </v-card>
      </v-col>

      <!-- Fila en foco -->
      <v-col cols="12" md="5">
        <v-card v-if="actual" outlined class="pa-4 panel">
          <div class="font-weight-bold mb-1">{{ actual.descripcion }}</div>
          <div class="caption grey--text mb-3">
            {{ actual.codigo }} · {{ actual.codigo_barras || "sin código de barras" }} ·
            costo {{ actual.costo !== null ? `$${actual.costo}` : "—" }}
            <span v-if="actual.porcentaje_iva !== null"> · IVA {{ actual.porcentaje_iva }}%</span>
          </div>
          <img v-if="actual.imagen_url" :src="actual.imagen_url" class="foto mb-3" />

          <v-alert v-for="a in actual.avisos" :key="a.tipo" dense outlined type="warning" class="caption mb-2">
            {{ textoAviso(a) }}
          </v-alert>

          <div v-if="actual.aplicada" class="caption grey--text">Esta fila ya se aplicó.</div>

          <template v-else>
            <!-- Producto existente -->
            <div class="caption grey--text text-uppercase font-weight-bold mb-1">¿Qué producto es?</div>
            <v-radio-group v-model="eleccion" dense hide-details class="mt-0">
              <v-radio
                v-for="c in actual.candidatos"
                :key="c.presentacion_id"
                :value="c.presentacion_id"
                :label="`${c.descripcion} · ${c.presentacion}${c.por === 'nombre' ? ` (${Math.round((c.puntaje || 0) * 100)}%)` : ''}`"
              />
              <v-radio v-if="otro" :value="otro.pp_id" :label="`${otro.descripcion} · ${otro.presentacion_nombre}`" />
            </v-radio-group>
            <v-autocomplete
              v-model="otro"
              :items="busquedaProductos"
              :search-input.sync="textoProducto"
              item-text="texto"
              return-object
              no-filter
              dense
              outlined
              hide-details
              clearable
              placeholder="Buscar otro producto"
              class="mt-2"
              @change="(v) => v && (eleccion = v.pp_id)"
            />
            <v-btn small depressed color="green" dark class="text-none mt-2" :disabled="!eleccion" @click="esEste">
              Es este
            </v-btn>

            <v-divider class="my-4" />

            <!-- Producto nuevo -->
            <div class="caption grey--text text-uppercase font-weight-bold mb-2">O es nuevo</div>
            <v-text-field v-model="nuevo.nuevo_nombre" dense outlined label="Nombre" />
            <div class="d-flex" style="gap: 8px">
              <v-text-field
                v-model="nuevo.nuevo_margen"
                dense
                outlined
                label="Margen"
                suffix="%"
                :hint="actual.sugerencias.margen_origen ? `Por ${actual.sugerencias.margen_origen}` : ''"
                persistent-hint
              />
              <v-select
                v-model="nuevo.nuevo_alicuota"
                :items="alicuotas"
                item-text="descripcion"
                item-value="id"
                dense
                outlined
                clearable
                label="IVA"
                :hint="actual.sugerencias.alicuota_origen ? `De la ${actual.sugerencias.alicuota_origen}` : ''"
                persistent-hint
              />
            </div>
            <v-select
              v-model="nuevo.nuevo_categoria"
              :items="categorias"
              item-text="nombre"
              item-value="id"
              dense
              outlined
              clearable
              label="Categoría"
              class="mt-2"
            />
            <div class="d-flex" style="gap: 8px">
              <v-btn small depressed color="blue" dark class="text-none" @click="esNuevo">Dar de alta</v-btn>
              <v-btn small text class="text-none" @click="decidir({ decision: 'IGNORAR' })">Ignorar</v-btn>
              <v-btn v-if="actual.decision" small text class="text-none" @click="decidir({ decision: '' })">Deshacer</v-btn>
            </div>

            <!-- También lo tienen -->
            <v-divider class="my-4" />
            <v-btn small text class="text-none px-0" :loading="cargandoOtros" @click="fetchOtros">
              ¿Quién más lo tiene?
            </v-btn>
            <div v-for="o in otros" :key="o.item_id" class="d-flex align-center caption mt-1">
              <span class="flex-grow-1">{{ o.proveedor }} · ${{ o.precio }} · {{ o.lista }} ({{ o.fecha }})</span>
              <v-btn x-small text class="text-none" :disabled="!actual.presentacion" @click="confirmarOtro(o)">
                Lo tiene
              </v-btn>
            </div>
            <div v-if="otrosCargados && !otros.length" class="caption grey--text">Nadie más en las últimas listas.</div>
          </template>
        </v-card>
      </v-col>
    </v-row>

    <!-- Vista previa -->
    <v-dialog v-model="dialogPrevia" max-width="640">
      <v-card v-if="previa">
        <v-card-title>Vista previa</v-card-title>
        <v-card-text>
          <div class="mb-2">
            Se dan de alta <b>{{ previa.nuevos.length }}</b> productos
            <span v-if="previa.fotos">({{ previa.fotos }} con foto del catálogo)</span>,
            se actualiza el costo de <b>{{ previa.cambios_de_costo.length }}</b> y se ignoran {{ previa.ignoradas }}.
            En: {{ previa.sucursales.join(", ") }}.
          </div>
          <v-alert v-if="previa.pendientes" dense outlined type="info" class="caption">
            {{ previa.pendientes }} filas quedan sin aplicar porque no están resueltas.
          </v-alert>
          <div v-if="cambiosGrandes.length" class="mt-3">
            <div class="caption font-weight-bold mb-1">Costos que cambian mucho</div>
            <div v-for="c in cambiosGrandes" :key="c.linea_id" class="caption">
              {{ c.descripcion }}: ${{ c.aviso.antes }} → ${{ c.aviso.ahora }} ({{ c.aviso.variacion }}%)
            </div>
          </div>
          <div v-if="previa.nuevos.length" class="mt-3">
            <div class="caption font-weight-bold mb-1">Nuevos</div>
            <div v-for="n in previa.nuevos.slice(0, 20)" :key="n.linea_id" class="caption">
              {{ n.nombre }} · costo ${{ n.costo }} · margen {{ n.margen }}%
            </div>
            <div v-if="previa.nuevos.length > 20" class="caption grey--text">y {{ previa.nuevos.length - 20 }} más</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text class="text-none" @click="dialogPrevia = false">Volver</v-btn>
          <v-btn
            depressed
            color="primary"
            class="text-none"
            :loading="aplicando"
            :disabled="!previa.nuevos.length && !previa.cambios_de_costo.length && !previa.ignoradas"
            @click="aplicar"
          >
            Aplicar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CategoriasAPI from "@/api/categorias";
import { getApiErrorMessage } from "@/util/apiError";

const POR_PAGINA = 100;

function aplanar(nodos, prefijo = "") {
  return (nodos || []).flatMap((n) => [
    { id: n.id, nombre: prefijo + n.name },
    ...aplanar(n.children, `${prefijo}${n.name} › `),
  ]);
}

/**
 * Mesa del centro de carga (F7): cada fila de la lista se resuelve contra el
 * catálogo (es este producto / es nuevo / se ignora) y se aplica todo junto.
 */
export default {
  name: "ProductosCargaMesa",
  data() {
    return {
      carga: null,
      lineas: [],
      total: 0,
      pagina: 1,
      loading: false,
      filtro: "sin_decidir",
      busqueda: "",
      foco: 0,
      seleccion: [],
      eleccion: null,
      otro: null,
      textoProducto: null,
      busquedaProductos: [],
      nuevo: {},
      otros: [],
      otrosCargados: false,
      cargandoOtros: false,
      categorias: [],
      alicuotas: [],
      loteMargen: "",
      loteCategoria: null,
      loteAlicuota: null,
      manual: { descripcion: "", codigo_barras: "", costo: "" },
      dialogPrevia: false,
      previa: null,
      aplicando: false,
      debounce: null,
      debounceProducto: null,
      proveedores: [],
      proveedorFactura: null,
      buscarProveedor: null,
      debounceProveedor: null,
    };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    actual() {
      return this.lineas[this.foco] || null;
    },
    esFactura() {
      return this.carga && this.carga.origen === "FACTURA";
    },
    puedeRegistrarCompra() {
      return this.esFactura && this.carga.proveedor && this.carga.conteos.aplicadas === this.carga.conteos.total;
    },
    cambiosGrandes() {
      return (this.previa ? this.previa.cambios_de_costo : []).filter((c) => c.aviso);
    },
  },
  watch: {
    actual: {
      immediate: true,
      handler(l) {
        this.otro = null;
        this.otros = [];
        this.otrosCargados = false;
        if (!l) return;
        this.eleccion = l.presentacion || (l.candidatos[0] && l.candidatos[0].presentacion_id) || null;
        const s = l.sugerencias || {};
        this.nuevo = {
          nuevo_nombre: l.nuevo_nombre || s.nombre || l.descripcion,
          nuevo_margen: l.nuevo_margen !== null ? l.nuevo_margen : s.margen,
          nuevo_alicuota: l.nuevo_alicuota || s.alicuota_id || null,
          nuevo_categoria: l.nuevo_categoria || s.categoria_id || null,
        };
      },
    },
    buscarProveedor(texto) {
      clearTimeout(this.debounceProveedor);
      this.debounceProveedor = setTimeout(async () => {
        const { data } = await this.axios.get("/proveedores/", { params: { page: 1, page_size: 20, search: texto || undefined } });
        this.proveedores = data.results || data;
      }, 300);
    },
    textoProducto(texto) {
      clearTimeout(this.debounceProducto);
      if (!texto || texto.length < 2) return;
      this.debounceProducto = setTimeout(() => this.buscarProductos(texto), 300);
    },
  },
  methods: {
    colorEstado(estado) {
      return { SEGURO: "green", DUDOSO: "orange", NUEVO: "blue" }[estado];
    },
    textoAviso(a) {
      if (a.tipo === "costo") return `El costo pasa de $${a.antes} a $${a.ahora} (${a.variacion}%)`;
      if (a.tipo === "iva") return `La lista dice IVA ${a.lista}, el producto tiene ${a.producto}`;
      if (a.tipo === "otro_proveedor") return `${a.proveedor} lo tiene a $${a.costo}${a.fecha ? ` (${a.fecha})` : ""}`;
      return a.tipo;
    },
    params() {
      const p = { page: this.pagina, page_size: POR_PAGINA, pendientes: 1 };
      if (this.filtro === "sin_decidir") p.decision = "";
      else if (this.filtro !== "todas") p.estado = this.filtro;
      if (this.busqueda) p.q = this.busqueda;
      return p;
    },
    async fetchCarga() {
      const { data } = await this.axios.get(`/productos/cargas/${this.id}`);
      this.carga = data;
    },
    async fetchLineas(agregar = false) {
      this.loading = true;
      try {
        const { data } = await this.axios.get(`/productos/cargas/${this.id}/lineas`, { params: this.params() });
        this.lineas = agregar ? [...this.lineas, ...data.results] : data.results;
        this.total = data.count;
      } finally {
        this.loading = false;
      }
    },
    recargar() {
      this.pagina = 1;
      this.foco = 0;
      this.seleccion = [];
      this.fetchLineas();
      this.fetchCarga();
    },
    masFilas() {
      this.pagina += 1;
      this.fetchLineas(true);
    },
    buscarConPausa() {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(this.recargar, 400);
    },
    alternar(l) {
      this.seleccion = this.seleccion.includes(l.id)
        ? this.seleccion.filter((id) => id !== l.id)
        : [...this.seleccion, l.id];
    },
    reemplazar(linea) {
      const i = this.lineas.findIndex((l) => l.id === linea.id);
      if (i >= 0) this.$set(this.lineas, i, linea);
    },
    async decidir(cambios, avanzar = true) {
      const linea = this.actual;
      if (!linea || linea.aplicada) return;
      try {
        const { data } = await this.axios.patch(`/productos/cargas/${this.id}/lineas/${linea.id}`, {
          version: linea.version,
          ...cambios,
        });
        this.reemplazar(data);
        if (avanzar && this.foco < this.lineas.length - 1) this.foco += 1;
        this.fetchCarga();
      } catch (e) {
        const data = e.response && e.response.data;
        if (e.response && e.response.status === 409 && data.linea) this.reemplazar(data.linea);
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          color: "error",
          text: getApiErrorMessage(e, "No se pudo guardar la fila"),
          timeout: 5000,
        });
      }
    },
    esEste() {
      this.decidir({ decision: "EXISTENTE", presentacion: this.eleccion });
    },
    esNuevo() {
      this.decidir({ decision: "NUEVO", ...this.nuevo });
    },
    aceptarPropuesta() {
      const l = this.actual;
      if (!l) return;
      if (l.estado === "NUEVO") this.esNuevo();
      else if (this.eleccion) this.esEste();
    },
    teclado(e) {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "ArrowDown") {
        this.foco = Math.min(this.foco + 1, this.lineas.length - 1);
        e.preventDefault();
      } else if (e.key === "ArrowUp") {
        this.foco = Math.max(this.foco - 1, 0);
        e.preventDefault();
      } else if (e.key === "Enter") {
        this.aceptarPropuesta();
      } else if (e.key === "n" || e.key === "N") {
        this.esNuevo();
      } else if (e.key === "i" || e.key === "I") {
        this.decidir({ decision: "IGNORAR" });
      } else if (e.key === " " && this.actual) {
        this.alternar(this.actual);
        e.preventDefault();
      }
    },
    async lote(cambios) {
      try {
        const { data } = await this.axios.post(`/productos/cargas/${this.id}/lote`, { ids: this.seleccion, cambios });
        this.$updateBus.$emit("showSnackBar", { show: true, color: "success", text: `${data.editadas} filas actualizadas` });
        this.recargar();
      } catch (e) {
        this.$updateBus.$emit("showSnackBar", { show: true, color: "error", text: getApiErrorMessage(e, "No se pudo editar") });
      }
    },
    loteValores() {
      const cambios = {};
      if (this.loteMargen) cambios.nuevo_margen = this.loteMargen;
      if (this.loteCategoria) cambios.nuevo_categoria = this.loteCategoria;
      if (this.loteAlicuota) cambios.nuevo_alicuota = this.loteAlicuota;
      this.lote(cambios);
    },
    async aceptarSeguras() {
      const { data } = await this.axios.post(`/productos/cargas/${this.id}/aceptar-seguras`);
      this.$updateBus.$emit("showSnackBar", { show: true, color: "success", text: `${data.aceptadas} filas aceptadas` });
      this.recargar();
    },
    async agregarManual() {
      try {
        await this.axios.post(`/productos/cargas/${this.id}/lineas`, this.manual);
        this.manual = { descripcion: "", codigo_barras: "", costo: "" };
        this.recargar();
      } catch (e) {
        this.$updateBus.$emit("showSnackBar", { show: true, color: "error", text: getApiErrorMessage(e, "No se pudo agregar") });
      }
    },
    async buscarProductos(texto) {
      const { data } = await this.axios.get("/productos/psp", { params: { q: texto, page: 1, page_size: 20 } });
      this.busquedaProductos = (data.results || data).map((p) => ({
        pp_id: p.pp_id,
        descripcion: p.descripcion,
        presentacion_nombre: p.presentacion_nombre,
        texto: `${p.descripcion} · ${p.presentacion_nombre}`,
      }));
    },
    async fetchOtros() {
      this.cargandoOtros = true;
      try {
        const { data } = await this.axios.get(`/productos/cargas/${this.id}/lineas/${this.actual.id}/tambien-lo-tienen`);
        this.otros = data;
        this.otrosCargados = true;
      } finally {
        this.cargandoOtros = false;
      }
    },
    async confirmarOtro(o) {
      const { data } = await this.axios.post(
        `/productos/cargas/${this.id}/lineas/${this.actual.id}/tambien-lo-tienen`,
        { item_id: o.item_id }
      );
      this.otros = data;
      this.$updateBus.$emit("showSnackBar", { show: true, color: "success", text: `Quedó en el mapa de ${o.proveedor}` });
    },
    async elegirProveedor(id) {
      if (!id) return;
      try {
        const { data } = await this.axios.patch(`/productos/cargas/${this.id}`, { proveedor: id });
        this.carga = data;
        this.recargar();
      } catch (e) {
        this.$updateBus.$emit("showSnackBar", { show: true, color: "error", text: getApiErrorMessage(e, "No se pudo elegir el proveedor") });
      }
    },
    // La compra se registra en el formulario de siempre, precargado con la factura
    registrarCompra() {
      const sucursal = this.carga.sucursales_detalle[0];
      this.$router.push({
        name: "empresa.sucursal.compras.factura.registrar",
        params: { uuid_sucursal: sucursal.uuid },
        query: { carga: this.carga.id },
      });
    },
    async abrirVistaPrevia() {
      const { data } = await this.axios.get(`/productos/cargas/${this.id}/vista-previa`);
      this.previa = data;
      this.dialogPrevia = true;
    },
    async aplicar() {
      this.aplicando = true;
      try {
        const { data } = await this.axios.post(`/productos/cargas/${this.id}/aplicar`);
        this.dialogPrevia = false;
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          color: "success",
          text: `Listo: ${data.nuevos} productos nuevos, ${data.actualizados} costos actualizados.`,
          timeout: 6000,
        });
        this.recargar();
      } catch (e) {
        this.$updateBus.$emit("showSnackBar", { show: true, color: "error", text: getApiErrorMessage(e, "No se pudo aplicar") });
      } finally {
        this.aplicando = false;
      }
    },
    async fetchOpciones() {
      const [{ data: opciones }, { data: categorias }] = await Promise.all([
        this.axios.get("/productos/productos/opciones"),
        CategoriasAPI.index(),
      ]);
      this.alicuotas = opciones.alicuotas_iva || [];
      const raiz = categorias.parent || categorias;
      this.categorias = aplanar(raiz.children || []);
    },
  },
  created() {
    this.fetchCarga();
    this.fetchLineas();
    this.fetchOpciones();
  },
  mounted() {
    this.$refs.mesa.focus();
  },
};
</script>

<style scoped>
.mesa tbody tr {
  cursor: pointer;
}
.fila--foco {
  background: rgba(62, 166, 255, 0.12);
}
.fila--aplicada {
  opacity: 0.5;
}
.panel {
  position: sticky;
  top: 16px;
}
.foto {
  max-width: 120px;
  max-height: 120px;
  object-fit: contain;
  border-radius: 8px;
}
</style>
